'use client';
import { useState, useEffect } from 'react';
import { atom } from './store.js';

/**
 * Хук `useAtom` предоставляет реактивное состояние, которое синхронизируется с внешним атомом состояния.
 *
 * @template T Тип состояния.
 *
 * @param {string} key - Ключ для идентификации атома состояния.
 * @param {T} initialState - Начальное состояние, которое будет установлено в атоме.
 *
 * @returns {[T, (newState: T) => void]} Массив, содержащий текущее состояние и функцию для его обновления.
 *
 * Возвращаемый массив содержит следующие элементы:
 * - Текущая локальная версия состояния.
 * - Функция, которая принимает новое состояние и обновляет как локальное, так и внешнее состояние атома.
 *
 * Хук отслеживает изменения во внешнем атоме состояния и обновляет локальное состояние всякий раз, когда внешнее состояние изменяется.
 *
 * @example:
 * ```
 * const [counter, setCounter] = useAtom('counter', 0);
 * ```
 */ const useAtom = (key, initialState)=>{
    const externalState = atom(key, initialState);
    const [localState, setLocalState] = useState(()=>externalState.get());
    useEffect(()=>{
        return externalState.subscribe((newState)=>{
            if (newState !== localState) {
                setLocalState(newState);
            }
        });
    }, [
        externalState,
        localState
    ]);
    const setAtomState = (newState)=>{
        setLocalState(newState);
        externalState.set(newState);
    };
    return [
        localState,
        setAtomState
    ];
};

export { useAtom };
//# sourceMappingURL=useAtom.js.map
